import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { Dropdown, Form, InputGroup, Spinner } from 'react-bootstrap';
import { ArrowDownUp, CurrencyExchange } from 'react-bootstrap-icons';
import {
  MANUAL_ORG_SOURCE_ID,
  useExchangeRateWidget,
} from '../../hooks/useExchangeRateWidget';
import { SUPPORTED_CURRENCIES } from '../../models/organization';
import { formatDateTimeEs } from '../../utils/format';
import { formatExchangeRate } from '../../utils/formatExchangeRate';
import styles from './ExchangeRatePopover.module.scss';

const RateChipToggle = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    compact?: boolean;
    bsPrefix?: string;
  }
>(({ children, className, compact, bsPrefix: _bsPrefix, ...props }, ref) => (
  <button
    type="button"
    ref={ref}
    className={`${styles.chip} ${compact ? styles.chipCompact : ''} ${
      className || ''
    }`}
    {...props}
  >
    {children}
  </button>
));
RateChipToggle.displayName = 'RateChipToggle';

function currencySymbol(code: string): string {
  const match = SUPPORTED_CURRENCIES.find(c => c.code === code);
  return match?.symbol || code;
}

function currencyShortLabel(code: string): string {
  if (code === 'VES') return 'Bs';
  if (code === 'USD') return 'USD';
  return code;
}

function parseAmount(raw: string): number | null {
  const normalized = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
}

function formatCalcAmount(value: number): string {
  if (!Number.isFinite(value)) return '';
  const abs = Math.abs(value);
  const digits = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
  return value.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: digits,
  });
}

interface ExchangeRatePopoverProps {
  organizationId: string | null | undefined;
  compact?: boolean;
}

const ExchangeRatePopover: React.FC<ExchangeRatePopoverProps> = ({
  organizationId,
  compact = false,
}) => {
  const {
    loading,
    currency,
    referenceCurrency,
    sources,
    selectedSourceId,
    setSelectedSourceId,
    activeRate,
    activeSourceName,
    lastFetchedAt,
  } = useExchangeRateWidget(organizationId);

  const [show, setShow] = useState(false);
  const [referenceFirst, setReferenceFirst] = useState(true);
  const [referenceInput, setReferenceInput] = useState('1');
  const [localInput, setLocalInput] = useState('');

  const refLabel = currencyShortLabel(referenceCurrency);
  const localLabel = currencyShortLabel(currency);
  const refSymbol = currencySymbol(referenceCurrency);
  const localSymbol = currencySymbol(currency);

  useEffect(() => {
    if (activeRate == null || activeRate <= 0) {
      setLocalInput('');
      return;
    }
    const ref = parseAmount(referenceInput);
    if (ref == null) {
      setLocalInput('');
      return;
    }
    setLocalInput(formatCalcAmount(ref * activeRate));
    // Only recompute local when rate/source changes; keep typed reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRate, selectedSourceId]);

  const chipRateText = useMemo(() => {
    if (loading) return '…';
    if (activeRate == null) return '—';
    return `1 ${refLabel} = ${formatExchangeRate(activeRate)} ${localLabel}`;
  }, [loading, activeRate, refLabel, localLabel]);

  const handleReferenceChange = (raw: string) => {
    setReferenceInput(raw);
    if (activeRate == null || activeRate <= 0) {
      setLocalInput('');
      return;
    }
    const value = parseAmount(raw);
    if (value == null) {
      setLocalInput('');
      return;
    }
    setLocalInput(formatCalcAmount(value * activeRate));
  };

  const handleLocalChange = (raw: string) => {
    setLocalInput(raw);
    if (activeRate == null || activeRate <= 0) {
      setReferenceInput('');
      return;
    }
    const value = parseAmount(raw);
    if (value == null) {
      setReferenceInput('');
      return;
    }
    setReferenceInput(formatCalcAmount(value / activeRate));
  };

  const handleSwap = () => {
    setReferenceFirst(prev => !prev);
  };

  const selectOptions = useMemo(() => {
    if (sources.length === 0) {
      return [
        {
          id: MANUAL_ORG_SOURCE_ID,
          label: 'Tasa de la organización',
        },
      ];
    }
    return sources.map(src => ({
      id: src.id,
      label:
        (src.name && src.name.trim()) ||
        src.sourceUrl ||
        src.id,
    }));
  }, [sources]);

  const referenceField = (
    <Form.Group className="mb-2" controlId="fx-ref-amount">
      <Form.Label className={styles.fieldLabel}>{refLabel}</Form.Label>
      <InputGroup size="sm">
        <InputGroup.Text className={styles.affix}>{refSymbol}</InputGroup.Text>
        <Form.Control
          type="text"
          inputMode="decimal"
          value={referenceInput}
          onChange={e => handleReferenceChange(e.target.value)}
          placeholder="0"
          aria-label={`Cantidad en ${refLabel}`}
        />
      </InputGroup>
    </Form.Group>
  );

  const localField = (
    <Form.Group className="mb-2" controlId="fx-local-amount">
      <Form.Label className={styles.fieldLabel}>{localLabel}</Form.Label>
      <InputGroup size="sm">
        <InputGroup.Text className={styles.affix}>{localSymbol}</InputGroup.Text>
        <Form.Control
          type="text"
          inputMode="decimal"
          value={localInput}
          onChange={e => handleLocalChange(e.target.value)}
          placeholder="0"
          aria-label={`Cantidad en ${localLabel}`}
        />
      </InputGroup>
    </Form.Group>
  );

  const chipTitle =
    activeSourceName && activeRate != null
      ? `${activeSourceName} · ${formatExchangeRate(activeRate)}`
      : activeSourceName || 'Tipo de cambio';

  return (
    <Dropdown
      show={show}
      onToggle={next => setShow(Boolean(next))}
      align="end"
      autoClose="outside"
    >
      <Dropdown.Toggle
        as={RateChipToggle}
        compact={compact}
        aria-label="Tipo de cambio"
        title={chipTitle}
      >
        <CurrencyExchange className={styles.chipIcon} aria-hidden />
        {!compact && <span className={styles.chipText}>{chipRateText}</span>}
        {compact && (
          <span className={styles.chipTextCompact}>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : activeRate != null ? (
              formatExchangeRate(activeRate)
            ) : (
              '—'
            )}
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className={styles.menu} renderOnMount>
        <div className={styles.panel} onClick={e => e.stopPropagation()}>
          <div className={styles.panelTitle}>Tipo de cambio</div>

          <Form.Group className="mb-2" controlId="fx-source">
            <Form.Label className={styles.fieldLabel}>Fuente</Form.Label>
            <Form.Select
              size="sm"
              value={selectedSourceId || MANUAL_ORG_SOURCE_ID}
              onChange={e => setSelectedSourceId(e.target.value)}
              disabled={loading || selectOptions.length === 0}
            >
              {selectOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <div className={styles.rateBlock}>
            {loading ? (
              <div className="d-flex align-items-center gap-2 text-secondary">
                <Spinner animation="border" size="sm" />
                <span>Cargando…</span>
              </div>
            ) : activeRate != null ? (
              <>
                <div className={styles.rateValue}>
                  1 {refLabel} = {formatExchangeRate(activeRate)} {localLabel}
                </div>
                {lastFetchedAt ? (
                  <div className={styles.rateMeta}>
                    Última actualización:{' '}
                    {formatDateTimeEs(lastFetchedAt)}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-secondary small">
                No hay tasa disponible
              </div>
            )}
          </div>

          <div className={styles.calcHeader}>
            <span>Calculadora</span>
            <button
              type="button"
              className={styles.swapBtn}
              onClick={handleSwap}
              aria-label="Invertir monedas"
              title="Invertir monedas"
            >
              <ArrowDownUp size={14} />
            </button>
          </div>

          {referenceFirst ? (
            <>
              {referenceField}
              {localField}
            </>
          ) : (
            <>
              {localField}
              {referenceField}
            </>
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ExchangeRatePopover;
